<script lang="ts">
	import type { Vehicle } from './vehicle'
	import type { Writable } from 'svelte/store'
	import type { SvelteAsr, Mediator } from 'types/common'
	import type { VehiclesLookup$result } from '$houdini'
	import type { VehicleMediatorProviders } from './basic/basic'
	import type { i18n } from 'types/common'

	import { graphql } from '$houdini'
	import { onMount, onDestroy, getContext } from 'svelte'
	import Button from '@isoftdata/svelte-button'
	import { hasPermission } from 'utility/permission'
	import { AsrNavTabBar } from '@isoftdata/svelte-nav-tab-bar'
	import LoadItemModal from '@isoftdata/svelte-load-item-modal'
	import { Dropdown, DropdownItem } from '@isoftdata/svelte-dropdown'
	import { PersistedUserState } from '@isoftdata/svelte-store-user-local-writable'
	import session from 'stores/session'

	interface Props {
		asr: SvelteAsr
		storeId: number
		vehicle: Vehicle
		stateName: string
		vehicleId: number | undefined
		parameters?: Record<string, string | undefined>
	}

	let { asr, storeId, vehicle, stateName, vehicleId, parameters = {} }: Props = $props()

	let isSaving = $state(false)
	let vehicleChanged = $state(true)
	let requiredFieldsComplete = $state(true)
	let loadVehicleModal: LoadItemModal<VehiclesLookup$result['vehicles']['items'][number]> = $state()!

	const { t: translate } = getContext<i18n>('i18next')
	const mediator = getContext<Mediator<VehicleMediatorProviders>>('mediator')

	const lastVehicleSearch = new PersistedUserState<Record<string, unknown>>(
		$session.userAccountId,
		'lastVehicleSearch',
		{},
	)
	let clearScreenOnSave: Writable<boolean>

	const vehicleLookupQuery = graphql(`
		query VehiclesLookup($filter: VehicleFilter) {
			vehicles(filter: $filter) {
				items {
					id
					store {
						id
						code
						name
					}
					stockNumber
					vin
					inventoryType {
						id
						name
					}
				}
			}
		}
	`)

	const vehicleChangedUnsubscribe = mediator.provide('vehicleChanged', hasChanged => (vehicleChanged = hasChanged))
	const requiredVehicleFieldsCompleteUnsubscribe = mediator.provide(
		'requiredVehicleFieldsComplete',
		fieldsComplete => (requiredFieldsComplete = fieldsComplete),
	)

	onMount(() => {
		if (vehicle?.stockNumber) {
			mediator.publish('activity', {
				title: `#${vehicle.stockNumber}`,
				type: 'VEHICLE',
				route: stateName,
				name: 'Vehicle',
				parameters,
				icon: 'truck',
			})
		}
	})

	onDestroy(() => {
		vehicleChangedUnsubscribe?.()
		requiredVehicleFieldsCompleteUnsubscribe?.()
	})
</script>

<div class="card mb-3">
	<div class="card-header">
		<div class="d-flex flex-wrap justify-content-between">
			<div class="me-auto">
				<h4 class="inline">
					<i class="fas fa-truck"></i>
					{#if vehicle?.id}
						<span class="d-inline-block">
							<span class="bold">{translate('common:stockNum', 'Stock #')}:</span>
							{vehicle.stockNumber}
						</span>
						<span class="d-inline-block">
							<span class="bold">{translate('common:make', 'Make')}:</span>
							{vehicle.manufacturer?.name ?? ''}
						</span>
						<span class="d-inline-block">
							<span class="bold">{translate('common:model', 'Model')}:</span>
							{vehicle.model?.name ?? ''}
						</span>
					{:else}
						{translate('vehicleSearch.newVehicle', 'New Vehicle')}
					{/if}
				</h4>
			</div>
			<div
				class="d-flex flex-wrap d-sm-block mb-2 mb-sm-0"
				style="gap: 5px"
			>
				{#if lastVehicleSearch.current}
					<Button
						outline
						icon="history"
						size="sm"
						href={asr.makePath('app.vehicle-search.results', lastVehicleSearch.current)}
					>
						{translate('vehicle.backToSearch', 'Back to Search')}
					</Button>
				{/if}
				<Button
					outline
					size="sm"
					iconClass="plus"
					color="success"
					href={asr.makePath(null, { storeId, lastResetTime: Date.now() })}
					>{translate('vehicleSearch.newVehicle', 'New Vehicle')}</Button
				>
				<Button
					outline
					size="sm"
					iconClass="magnifying-glass"
					title={translate('vehicle.clickToSearchForVehiclesByStock#', 'Click to search for vehicles by Stock #')}
					onclick={() => loadVehicleModal.open()}
				></Button>
				<Dropdown
					split
					menuItemClickCloses={false}
					size="sm"
					class="flex-grow-1"
					iconClass="floppy-disk"
					isLoading={isSaving}
					color={requiredFieldsComplete ? 'primary' : 'danger'}
					disabled={!requiredFieldsComplete || !vehicleChanged}
					colorGreyDisabled={requiredFieldsComplete}
					title={!requiredFieldsComplete
						? translate('error.interchange.missingRequiredHeading', 'Missing Required Fields')
						: ''}
					onclick={async () => {
						isSaving = true
						await mediator.publish('saveVehicle')
						isSaving = false
					}}
					>{translate('vehicle.saveVehicle', 'Save Vehicle')}{#if !requiredFieldsComplete}*{/if}
					{#snippet dropdownItems()}
						<h6 class="dropdown-header">{translate('common:saveAndDotDotDot', 'Save and...')}</h6>
						<DropdownItem
							icon={{
								prefix: 'far',
								icon: $clearScreenOnSave ? 'check-square' : 'square',
							}}
							onclick={() => ($clearScreenOnSave = !$clearScreenOnSave)}
						>
							{translate('vehicle.startNewVehicle', 'Start New Vehicle')} ({translate(
								'vehicle.clearScreen',
								'Clear Screen',
							)})
						</DropdownItem>
					{/snippet}
				</Dropdown>
			</div>
		</div>

		<AsrNavTabBar
			{asr}
			tabs={[
				{
					title: translate('common:basic', 'Basic'),
					name: 'app.vehicle.basic',
					inherit: true,
					disabled: !vehicleId,
				},
				{
					title: translate('common:attachments', 'Attachments'),
					name: 'app.vehicle.attachment',
					inherit: true,
					disabled: !vehicleId || !hasPermission('VEHICLES_CAN_EDIT_ATTACHMENTS', storeId),
				},
				//Desktop app calls this tab "Performance", but we renamed it "History" for web
				{
					title: translate('common:history', 'History'),
					name: 'app.vehicle.history',
					inherit: true,
					disabled: !vehicleId || !hasPermission('VEHICLES_CAN_VIEW_VEHICLE_PERFORMANCE', storeId),
				},
				{
					title: translate('common:teardown', 'Teardown'),
					name: 'app.vehicle.teardown',
					inherit: true,
					disabled: !vehicleId || !hasPermission('VEHICLES_CAN_EDIT_BIDS', storeId),
				},
				{
					title: translate('vehicle.breakEvenGraph', 'Break Even Graph'),
					name: 'app.vehicle.break-even',
					inherit: true,
					// permission?
					disabled: !vehicleId,
				},
			]}
		/>
	</div>
	<div class="card-body">
		<uiView></uiView>
	</div>
</div>

<LoadItemModal
	itemIdProp="id"
	itemDisplayProp="stockNumber"
	title={translate('vehicle.vehiclesByStockNumber', 'Vehicles by Stock Number')}
	bind:this={loadVehicleModal}
	doSearch={async searchString => {
		const { data } = await vehicleLookupQuery.fetch({
			variables: {
				filter: {
					stockNumber: searchString,
				},
			},
		})
		return data?.vehicles.items ?? []
	}}
	lookupError={error =>
		mediator.publish('showMessage', {
			heading: translate('vehicle.errorFetchingVehicles', 'Error Fetching Vehicles'),
			message: error.message,
			type: 'danger',
			time: false,
		})}
	chooseItem={({ item }) => asr.go(null, { vehicleId: item.id, storeId: item.store.id })}
>
	{#snippet children({ item })}
		<div class="w-100 d-flex justify-content-between">
			<h5 class="mb-1">{item.stockNumber}</h5>
			<h5
				class="mb-1"
				title={item.store.name}
			>
				{translate('common:store', 'Store')}
				{item.store.code}
			</h5>
		</div>
		<div class="w-100">{item.inventoryType?.id} - {item.inventoryType?.name}</div>
		<small>{translate('common:vin', 'VIN')}: {item.id}</small>
	{/snippet}
</LoadItemModal>
